import React, { useState } from "react";
import PropTypes from "prop-types";
import "../styles/sidebar.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from 'react-router-dom';  // Use useNavigate

import {
  faCopy,
  faArrowUpRightFromSquare,
  faNewspaper,
} from "@fortawesome/free-solid-svg-icons"; // ✅ Import faNewspaper
import html2canvas from "html2canvas";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// PostCard component definition
const PostCard = ({ post, onFocus, focusedPostId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate(); // Hook for programmatic navigation

  // ✅ Move context menu state inside PostCard
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);



  const formatCount = (count) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`; // Convert to 'M' and keep one decimal point
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`; // Convert to 'k' and keep one decimal point
    }
    return count.toString(); // Return as string if less than 1000
  };

  
 
  const getProfilePageUrl = (post) => {
    switch (post.platform.toLowerCase()) {
      case "facebook":
        // Link to Facebook profile
        return `https://facebook.com/${post.owner_id}`;
      case "instagram":
        // Link to Instagram profile
        return `https://instagram.com/${post.owner_username}`;
      case "twitter":
        // Link to Twitter profile
        return `https://twitter.com/${post.author_username}`;
      case "tiktok":
        // Link to TikTok profile
        return `https://tiktok.com/@${post.author_username}`;
      default:
        return "#"; // Fallback link
    }
  };


  const platformIcons = {
    facebook: [
      {
        icon: "👍",
        label: "Likes",
        count: formatCount(post.reactions_total_count || 0),
        tooltip: "Likes",
      },
      {
        icon: "💬",
        label: "Comments",
        count: formatCount(post.comments_count || 0),
        tooltip: "Search for comments",
        onClick: () => handleIconClick("Comments", post),  // Example click handler
      },
      {
        icon: "🔄",
        label: "Shares",
        count: formatCount(post.shares_count || 0),
        tooltip: "Shares",
      },
    ],
    instagram: [
      {
        icon: "❤️",
        label: "Likes",
        count: formatCount(post.likes_count || 0),
        tooltip: "Likes",
      },
      {
        icon: "💬",
        label: "Comments",
        count: formatCount(post.comments_count || 0),
        tooltip: "Search for comments",
        onClick: () => handleIconClick("Comments",post),  // Example click handler
      },
    ],
    twitter: [
      {
        icon: "👁️",
        label: "Views",
        count: formatCount(post.view_count || 0),
        tooltip: "Views",
      },
      {
        icon: "💬",
        label: "Quotes",
        count: formatCount(post.quote_count || 0),
        tooltip: "Quotes",
      },
      {
        icon: "↩️",
        label: "Replies",
        count: formatCount(post.reply_count || 0),
        tooltip: "Replies",
      },
      {
        icon: "❤️",
        label: "Likes",
        count: formatCount(post.favorite_count || 0),
        tooltip: "Likes",
      },
      {
        icon: "🔁",
        label: "Retweets",
        count: formatCount(post.retweet_count || 0),
        tooltip: "Retweets",
      },
    ],
    tiktok: [
      {
        icon: "❤️",
        label: "Likes",
        count: formatCount(post.digg_count || 0),
        tooltip: "Likes",
      },
      {
        icon: "❤️",
        label: "Play",
        count: formatCount(post.play_count || 0),
        tooltip: "Play",
      },
      {
        icon: "💬",
        label: "Comments",
        count: formatCount(post.comment_count || 0),
        tooltip: "Search for comments",
        onClick: () => handleIconClick("Comments",post),  // Example click handler
      },
      {
        icon: "🔄",
        label: "Shares",
        count: formatCount(post.share_count || 0),
        tooltip: "Shares",
      },
    ],
  };

  // Determine display names based on platform
  let displayName, displayUsername;
  switch (post.platform.toLowerCase()) {
    case "facebook":
      displayName = post.owner_full_name || "Anonymous";
      displayUsername = post.owner_username || "@unknown";
      break;
    case "instagram":
      displayUsername = post.owner_username || "@unknown";
      break;
    case "twitter":
      displayUsername = post.author_username || "@unknown";
      break;
    case "tiktok":
      displayName = post.author_full_name || "Anonymous";
      displayUsername = post.author_username || "@unknown";
      break;
    default:
      displayName = "Anonymous";
      displayUsername = "@unknown";
  }

  const generateSocialURL = (post) => {
    const platform = post.platform.toLowerCase();
  
    // Ensure that the post.id is only used in the context of the correct platform
    switch (platform) {
      case "instagram":
        return `https://instagram.com/p/${post.shortcode}`;
      case "facebook":
        return `https://www.facebook.com/${post.id}`;
      case "twitter":
        return `https://twitter.com/${post.author_id}/status/${post.id}`;
      case "tiktok":
        return `https://tiktok.com/@${post.author_username}/video/${post.id}`;
      default:
        return "#";
    }
  };
  
  const copyPostLink = (postLink) => {
    navigator.clipboard.writeText(postLink).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Hide "Copied!" message after 2 seconds
    });
  };

  const handleIconClick = (label, post) => {
    // Check if the "Comments" icon was clicked
    if (label === "Comments") {
      const postLink = generateSocialURL(post);
      // Navigate to the comment monitoring page and pass the post link in the state
      navigate('/comment-monitoring', { state: { postLink: postLink } });
  
      // Copy the post link to the clipboard
      copyPostLink(postLink);
    }
  
    // You can add more conditions based on other icons if needed
  };


  const icons = platformIcons[post.platform.toLowerCase()] || [];

  const toggleExpandText = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      
      <div
        id={`post-${post.id}`} 
        className={`post-card ${post.platform.toLowerCase()} ${
          focusedPostId === post.id ? "focused" : ""
        }`}
        onClick={() => onFocus(post.id)}
      >
     

        <div className="card-header">
          <a
            href={getProfilePageUrl(post)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // Prevent triggering the onClick of the parent div
          >
            <img
              src={`${
                process.env.PUBLIC_URL
              }/icons/${post.platform.toLowerCase()}.png`}
              alt={`${post.platform} profile`}
              className="user-img"
            />
          </a>

          <div className="header-info">
            <h3>{displayName}</h3>
            <p>{displayUsername}</p>
          </div>

          <div className="social-icon">
          <a
            href={generateSocialURL(post)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open Post"
            style={{ display: "inline-block" }} 
          >
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              style={{
                fontSize: "18px",
                color: "#f2f2f3",
                cursor: "pointer",
                transition: "color 0.3s ease-in-out",
              }}
              className="open-post-icon"
            />
          </a>

           {/* Copy Button with FontAwesome Icon */}
          <button
            className="copy-post-link-button"
            onClick={(e) => {
              e.stopPropagation();
              const postLink = generateSocialURL(post); 
              copyPostLink(postLink); 
            }}
            title="Copy Post Link"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>

          {copySuccess && <span className="copy-success">Copied!</span>}

        </div>

        </div>

        <div className="date-time">
  <p>
    {new Date(post.timestamp * 1000).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })}{" "}
    ,{" "}
    {new Date(post.timestamp * 1000).toLocaleTimeString("en-US", {
      timeZone: "Asia/Karachi",
    })}
  </p>
  <span className="sentiment-emoji" title="Happy 😊 70%">
    😊 70%
  </span>
</div>


        <div className="card-body">
          <p>
            {post.highlightedText ? (
              <span>{post.highlightedText}</span>
            ) : post.text && post.text.length > 100 ? (
              <React.Fragment>
              {isExpanded ? post.text : `${String(post.text).substring(0, 100)}...`}
              <button
                onClick={toggleExpandText}
                className="toggle-text-button-deck"
              >
                {isExpanded ? "Read Less..." : "Read More..."}
              </button>
            </React.Fragment>
            
            ) : (
              post.text || "No content available."
            )}
          </p>

          {(() => {
            // ✅ Extract Video URLs (Facebook & Instagram Fix Applied)
            const videoUrls =
              post.attached_videos_url && post.attached_videos_url.length
                ? post.attached_videos_url
                : post.attached_medias_url && post.attached_medias_url.length
                ? post.attached_medias_url.filter((url) => url.includes(".mp4"))
                : post.video_url
                ? [post.video_url]
                : post.video?.play_url
                ? [post.video.play_url]
                : post.attached_video_url // ✅ Instagram Video Fix
                ? [post.attached_video_url]
                : [];

            const imageUrls =
              post.platform && post.platform.toLowerCase() === "instagram"
                ? // ✅ Instagram: Use attached_media_display_url (either array or single string)
                  Array.isArray(post.attached_media_display_url)
                  ? post.attached_media_display_url
                  : post.attached_media_display_url
                  ? [post.attached_media_display_url]
                  : []
                : post.platform.toLowerCase() === "twitter"
                ? // ✅ Twitter: Use attached_medias_url (filter out videos)
                  post.attached_medias_url && post.attached_medias_url.length
                  ? post.attached_medias_url.filter(
                      (url) => !url.includes(".mp4")
                    )
                  : []
                : post.platform.toLowerCase() === "facebook"
                ? // ✅ Facebook: Use attached_image_url
                  post.attached_image_url
                  ? [post.attached_image_url]
                  : []
                : [];



            return (
              <>
                {/* ✅ Render Videos if Available */}
                {videoUrls.length > 0 && (
                  <div className="attached-videos">
                    {videoUrls.map((url, index) => (
                      <div key={index} className="video-container">
                        {/* ✅ TikTok Embed Fix */}
                        {post.platform.toLowerCase() === "tiktok" ? (
                          <iframe
                            key={index}
                            src={`https://www.tiktok.com/embed/${post.id}`}
                            title={`TikTok Video ${index + 1}`}
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            className="tiktok-iframe"
                            style={{
                              width: "100%", // Make it responsive and fit inside the card
                              height: "auto",
                              maxHeight: "450px", // You can adjust the max height
                              border: "none",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          ></iframe>
                        ) : (
                          <video controls className="post-video">
                            <source src={url} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ✅ Render Images if Available */}
                {imageUrls.length > 0 && (
                  <div
                    className={`attached-images ${
                      imageUrls.length === 3
                        ? "grid-3-special"
                        : `grid-${Math.min(imageUrls.length, 4)}`
                    }`}
                  >
                    {imageUrls.slice(0, 2).map(
                      (url, index) =>
                        url ? ( // ✅ Only show images that exist
                          <div
                            key={index}
                            className="image-container"
                            style={{ position: "relative", cursor: "pointer" }}
                            onClick={() => openModal(index)}
                          >
                            <img
                              src={url}
                              alt={`Post-image ${index + 1}`}
                              className="post-img"
                              onError={(e) => {
                                e.target.style.display = "none"; // ✅ Hide broken images instead of replacing
                              }}
                            />
                            {index === 1 && imageUrls.length > 2 && (
                              <div className="image-overlay">
                                +{imageUrls.length - 2}
                              </div>
                            )}
                          </div>
                        ) : null // ✅ Skip empty URLs
                    )}
                  </div>
                )}

                {/* ✅ Image Modal for Enlarged View (Only if Images Exist) */}
                {isModalOpen && imageUrls.length > 0 && (
                  <div
                    className="image-modal"
                    style={{ zIndex: isModalOpen ? 2147483647 : -1 }}
                  >
                    <div className="image-modal-content">
                      <button
                        className="image-close-modal"
                        onClick={closeModal}
                      >
                        &times;
                      </button>
                      <div className="modal-images">
                        {imageUrls.map((url, index) =>
                          url ? ( // ✅ Only show valid images
                            <img
                              key={index}
                              src={url}
                              alt={`Modal-image ${index + 1}`}
                              className="modal-img"
                              onError={(e) => {
                                e.target.style.display = "none"; // ✅ Hide broken images
                              }}
                            />
                          ) : null
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div className="card-footer">
          {icons.map((item, index) => (
            <div
              key={index}
              className="footer-item animated-icon"
              title={item.tooltip}
              onClick={item.onClick}
            >
              {item.icon} {item.count}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

PostCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    platform: PropTypes.string.isRequired,
    shortcode: PropTypes.string,
    author_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    author_username: PropTypes.string,
    owner_full_name: PropTypes.string,
    owner_username: PropTypes.string,
    owner_profile_image_url: PropTypes.string,
    text: PropTypes.string,
    attached_image_url: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    views_count: PropTypes.number,
    quotes_count: PropTypes.number,
    replies_count: PropTypes.number,
    reactions_total_count: PropTypes.number,
    comments_count: PropTypes.number,
    shares_count: PropTypes.number,
  }).isRequired,
  onFocus: PropTypes.func.isRequired,
  focusedPostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PostCard;
